// src/useAuth.ts
import { useCallback, useEffect, useSyncExternalStore } from "react";
import {
  getAccessToken,
  getApiBaseUrl,
  getImpersonatorId,
  loginApiV1AuthLoginPost,
  logoutApiV1AuthLogoutPost,
  meApiV1AuthMeGet,
  onSessionExpired,
  refreshApiV1AuthRefreshPost,
  registerApiV1AuthRegisterPost,
  setAccessToken
} from "@forge/api-client";
var IMPERSONATE_END_PATH = "/api/v1/admin/impersonate/end";
var TwoFactorRequiredError = class extends Error {
  constructor(tempToken) {
    super("Two-factor authentication is required to finish signing in.");
    this.tempToken = tempToken;
    this.name = "TwoFactorRequiredError";
  }
  tempToken;
};
var state = { user: null, isLoading: true, isImpersonating: false };
var listeners = /* @__PURE__ */ new Set();
function setState(next) {
  state = { ...state, ...next };
  for (const listener of listeners) {
    listener();
  }
}
function subscribe(listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
function getSnapshot() {
  return state;
}
function applySession(accessToken, user) {
  setAccessToken(accessToken);
  setState({ user, isImpersonating: getImpersonatorId(accessToken) !== null });
}
onSessionExpired(() => {
  setState({ user: null, isLoading: false, isImpersonating: false });
});
async function establishSession() {
  try {
    const { data: refreshed } = await refreshApiV1AuthRefreshPost({
      throwOnError: true
    });
    const accessToken = refreshed.data?.access_token ?? null;
    setAccessToken(accessToken);
    const { data: me } = await meApiV1AuthMeGet({ throwOnError: true });
    setState({
      user: me.data ?? null,
      isLoading: false,
      isImpersonating: getImpersonatorId(accessToken) !== null
    });
  } catch {
    setAccessToken(null);
    setState({ user: null, isLoading: false, isImpersonating: false });
  }
}
var bootstrapPromise = null;
function bootstrap() {
  bootstrapPromise ??= establishSession();
  return bootstrapPromise;
}
function useAuth() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot);
  useEffect(() => {
    void bootstrap();
  }, []);
  const login = useCallback(
    async (email, password) => {
      const { data: envelope } = await loginApiV1AuthLoginPost({
        body: { email, password },
        throwOnError: true
      });
      const payload = envelope.data;
      if (payload && "temp_token" in payload) {
        throw new TwoFactorRequiredError(payload.temp_token);
      }
      if (!payload) {
        throw new Error("Login response was empty.");
      }
      applySession(payload.access_token, payload.user);
      return payload.user;
    },
    []
  );
  const register = useCallback(
    async (email, password) => {
      await registerApiV1AuthRegisterPost({
        body: { email, password },
        throwOnError: true
      });
    },
    []
  );
  const logout = useCallback(async () => {
    try {
      await logoutApiV1AuthLogoutPost({ throwOnError: true });
    } finally {
      setAccessToken(null);
      setState({ user: null, isImpersonating: false });
    }
  }, []);
  const endImpersonation = useCallback(async () => {
    const token = getAccessToken();
    try {
      await fetch(`${getApiBaseUrl()}${IMPERSONATE_END_PATH}`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : void 0,
        credentials: "include"
      });
    } finally {
      setAccessToken(null);
    }
    await establishSession();
  }, []);
  const loadImpersonatedSession = useCallback(
    (accessToken, impersonatedUser) => {
      applySession(accessToken, impersonatedUser);
    },
    []
  );
  const completeTwoFactorLogin = useCallback(
    (accessToken, user) => {
      applySession(accessToken, user);
    },
    []
  );
  const applyUpdatedUser = useCallback((user) => {
    setState({ user });
  }, []);
  return {
    user: snapshot.user,
    isLoading: snapshot.isLoading,
    isImpersonating: snapshot.isImpersonating,
    login,
    logout,
    register,
    endImpersonation,
    loadImpersonatedSession,
    completeTwoFactorLogin,
    applyUpdatedUser
  };
}

// src/useOrg.ts
import { useCallback as useCallback2, useEffect as useEffect2, useSyncExternalStore as useSyncExternalStore2 } from "react";
import {
  listMembersApiV1OrgsOrgIdMembersGet,
  listMyOrgsApiV1OrgsGet,
  setCurrentOrgId
} from "@forge/api-client";
var STORAGE_KEY = "forge.currentOrgId";
function readStoredOrgId() {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}
function writeStoredOrgId(orgId) {
  try {
    window.localStorage.setItem(STORAGE_KEY, orgId);
  } catch {
  }
}
var state2 = { orgs: [], currentOrgId: null, role: null, isLoading: true };
var listeners2 = /* @__PURE__ */ new Set();
function setState2(next) {
  state2 = { ...state2, ...next };
  for (const listener of listeners2) {
    listener();
  }
}
function subscribe2(listener) {
  listeners2.add(listener);
  return () => {
    listeners2.delete(listener);
  };
}
function getSnapshot2() {
  return state2;
}
async function loadRole(userId, orgId) {
  try {
    const { data } = await listMembersApiV1OrgsOrgIdMembersGet({
      path: { org_id: orgId },
      query: { limit: 100 },
      throwOnError: true
    });
    const members = data.data ?? [];
    return members.find((member) => member.user_id === userId)?.role ?? null;
  } catch {
    return null;
  }
}
function pickOrgId(orgs) {
  const stored = readStoredOrgId();
  if (stored && orgs.some((org) => org.id === stored)) {
    return stored;
  }
  return orgs[0]?.id ?? null;
}
var loadedForUserId = null;
var loadGeneration = 0;
async function bootstrap2(userId) {
  loadedForUserId = userId;
  const generation = ++loadGeneration;
  setState2({ isLoading: true });
  try {
    const { data } = await listMyOrgsApiV1OrgsGet({ throwOnError: true });
    if (generation !== loadGeneration) {
      return;
    }
    const orgs = data.data ?? [];
    const currentOrgId = pickOrgId(orgs);
    setCurrentOrgId(currentOrgId);
    const role = currentOrgId ? await loadRole(userId, currentOrgId) : null;
    if (generation !== loadGeneration) {
      return;
    }
    setState2({ orgs, currentOrgId, role, isLoading: false });
  } catch {
    if (generation !== loadGeneration) {
      return;
    }
    setCurrentOrgId(null);
    setState2({ orgs: [], currentOrgId: null, role: null, isLoading: false });
  }
}
function useOrg() {
  const { user } = useAuth();
  const snapshot = useSyncExternalStore2(subscribe2, getSnapshot2);
  useEffect2(() => {
    if (!user) {
      if (loadedForUserId !== null) {
        loadedForUserId = null;
        loadGeneration += 1;
        setCurrentOrgId(null);
        setState2({ orgs: [], currentOrgId: null, role: null, isLoading: false });
      }
      return;
    }
    if (loadedForUserId !== user.id) {
      void bootstrap2(user.id);
    }
  }, [user]);
  const switchOrg = useCallback2(
    (orgId) => {
      writeStoredOrgId(orgId);
      setCurrentOrgId(orgId);
      setState2({ currentOrgId: orgId, role: null });
      const userId = user?.id;
      if (userId) {
        void loadRole(userId, orgId).then((role) => {
          if (state2.currentOrgId !== orgId) {
            return;
          }
          setState2({ role });
        });
      }
    },
    [user]
  );
  const refreshOrgs = useCallback2(async () => {
    if (user) {
      await bootstrap2(user.id);
    }
  }, [user]);
  const org = snapshot.orgs.find((candidate) => candidate.id === snapshot.currentOrgId) ?? null;
  return {
    orgs: snapshot.orgs,
    org,
    role: snapshot.role,
    isLoading: snapshot.isLoading,
    switchOrg,
    refreshOrgs
  };
}

// src/usePermission.ts
import { ROLE_PERMISSIONS } from "@forge/types";
function usePermission(permission) {
  const { role } = useOrg();
  if (!role) {
    return false;
  }
  return ROLE_PERMISSIONS[role].has(permission);
}

// src/useFeatureFlag.ts
import { useQuery } from "@tanstack/react-query";
import {
  listFeatureFlagsApiV1OrgsOrgIdFeatureFlagsGet,
  listFeatureFlagsApiV1OrgsOrgIdFeatureFlagsGetQueryKey
} from "@forge/api-client";
var STALE_TIME_MS = 6e4;
function useFeatureFlags() {
  const { org } = useOrg();
  return useQuery({
    queryKey: listFeatureFlagsApiV1OrgsOrgIdFeatureFlagsGetQueryKey({
      path: { org_id: org?.id ?? "" }
    }),
    queryFn: async () => {
      const { data } = await listFeatureFlagsApiV1OrgsOrgIdFeatureFlagsGet({
        path: { org_id: org?.id ?? "" },
        throwOnError: true
      });
      return data;
    },
    enabled: Boolean(org),
    staleTime: STALE_TIME_MS
  });
}
function useFeatureFlag(flag) {
  const { data } = useFeatureFlags();
  return Boolean(data?.data?.flags[flag]);
}

// src/useModules.ts
import { useEffect as useEffect3, useSyncExternalStore as useSyncExternalStore3 } from "react";
import { getConfigApiV1ConfigGet } from "@forge/api-client";
var DENY_BY_DEFAULT = {
  billing: false,
  notifications: false,
  fileStorage: false,
  featureFlags: false,
  gdpr: false,
  admin: false,
  oauth: false,
  totp2fa: false,
  multiOrg: false,
  usageMetering: false,
  outboundWebhooks: false,
  marketing: false,
  publicApi: false,
  enterpriseSso: false
};
function toActiveModules(modules) {
  return {
    billing: modules.billing,
    notifications: modules.notifications,
    fileStorage: modules.file_storage,
    featureFlags: modules.feature_flags,
    gdpr: modules.gdpr,
    admin: modules.admin,
    oauth: modules.oauth,
    totp2fa: modules.totp_2fa,
    multiOrg: modules.multi_org,
    usageMetering: modules.usage_metering,
    outboundWebhooks: modules.outbound_webhooks,
    marketing: modules.marketing,
    publicApi: modules.public_api,
    enterpriseSso: modules.enterprise_sso
  };
}
var UNRESOLVED_SUPPORT = { tool: "none", url: "" };
var NO_OAUTH_PROVIDERS = [];
var NO_MAINTENANCE = { enabled: false, message: "" };
var state3 = {
  modules: DENY_BY_DEFAULT,
  support: UNRESOLVED_SUPPORT,
  oauthProviders: NO_OAUTH_PROVIDERS,
  marketingUrl: "",
  banner: null,
  maintenance: NO_MAINTENANCE,
  signupDisabled: false
};
var listeners3 = /* @__PURE__ */ new Set();
function setState3(next) {
  state3 = next;
  for (const listener of listeners3) {
    listener();
  }
}
function subscribeConfig(listener) {
  listeners3.add(listener);
  return () => {
    listeners3.delete(listener);
  };
}
function getModulesSnapshot() {
  return state3.modules;
}
function getSupportSnapshot() {
  return state3.support;
}
function getOAuthProvidersSnapshot() {
  return state3.oauthProviders;
}
function getMarketingUrlSnapshot() {
  return state3.marketingUrl;
}
function getBannerSnapshot() {
  return state3.banner;
}
function getMaintenanceSnapshot() {
  return state3.maintenance;
}
function getSignupDisabledSnapshot() {
  return state3.signupDisabled;
}
var bootstrapPromise2 = null;
async function fetchConfig() {
  try {
    const { data: envelope } = await getConfigApiV1ConfigGet({
      throwOnError: true
    });
    if (envelope.data) {
      setState3({
        modules: toActiveModules(envelope.data.modules),
        support: {
          tool: envelope.data.support_tool,
          url: envelope.data.support_url
        },
        oauthProviders: envelope.data.oauth_providers ?? NO_OAUTH_PROVIDERS,
        marketingUrl: envelope.data.marketing_url ?? "",
        banner: envelope.data.banner ?? null,
        maintenance: envelope.data.maintenance,
        signupDisabled: envelope.data.signup_disabled ?? false
      });
    }
  } catch {
  }
}
function bootstrapModules() {
  bootstrapPromise2 ??= fetchConfig();
  return bootstrapPromise2;
}
function refreshConfig() {
  return fetchConfig();
}
function useModules() {
  useEffect3(() => {
    void bootstrapModules();
  }, []);
  return useSyncExternalStore3(subscribeConfig, getModulesSnapshot);
}
function useOAuthProviders() {
  useEffect3(() => {
    void bootstrapModules();
  }, []);
  return useSyncExternalStore3(subscribeConfig, getOAuthProvidersSnapshot);
}
function useMarketingUrl() {
  useEffect3(() => {
    void bootstrapModules();
  }, []);
  return useSyncExternalStore3(subscribeConfig, getMarketingUrlSnapshot);
}
function useSupportSettings() {
  useEffect3(() => {
    void bootstrapModules();
  }, []);
  return useSyncExternalStore3(subscribeConfig, getSupportSnapshot);
}
var POLL_INTERVAL_MS = 6e4;
var pollTimer = null;
var pollSubscribers = 0;
function usePolledConfig() {
  useEffect3(() => {
    void bootstrapModules();
    pollSubscribers += 1;
    pollTimer ??= setInterval(() => {
      void refreshConfig();
    }, POLL_INTERVAL_MS);
    return () => {
      pollSubscribers -= 1;
      if (pollSubscribers === 0 && pollTimer !== null) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };
  }, []);
}
function useGlobalBanner() {
  usePolledConfig();
  return useSyncExternalStore3(subscribeConfig, getBannerSnapshot);
}
function useMaintenance() {
  usePolledConfig();
  return useSyncExternalStore3(subscribeConfig, getMaintenanceSnapshot);
}
function useSignupDisabled() {
  useEffect3(() => {
    void bootstrapModules();
  }, []);
  return useSyncExternalStore3(subscribeConfig, getSignupDisabledSnapshot);
}

// src/useLocale.ts
import { useCallback as useCallback3, useEffect as useEffect4, useSyncExternalStore as useSyncExternalStore4 } from "react";
import i18next from "i18next";
import {
  getConfigApiV1ConfigGet as getConfigApiV1ConfigGet2,
  updateMeApiV1AuthMePatch
} from "@forge/api-client";
import { DEFAULT_LOCALE } from "@forge/i18n";
var supportedLocales = [DEFAULT_LOCALE];
var configListeners = /* @__PURE__ */ new Set();
function setSupportedLocales(next) {
  supportedLocales = next;
  for (const listener of configListeners) {
    listener();
  }
}
function subscribeConfig2(listener) {
  configListeners.add(listener);
  return () => {
    configListeners.delete(listener);
  };
}
function getSupportedLocalesSnapshot() {
  return supportedLocales;
}
var bootstrapPromise3 = null;
function bootstrapLocaleConfig() {
  bootstrapPromise3 ??= (async () => {
    try {
      const { data: envelope } = await getConfigApiV1ConfigGet2({
        throwOnError: true
      });
      const config = envelope.data;
      if (!config) {
        return;
      }
      setSupportedLocales(config.supported_locales);
      if (!config.supported_locales.includes(i18next.language)) {
        await i18next.changeLanguage(config.default_locale);
      }
    } catch {
    }
  })();
  return bootstrapPromise3;
}
function subscribeLanguageChanged(listener) {
  i18next.on("languageChanged", listener);
  return () => {
    i18next.off("languageChanged", listener);
  };
}
function getLanguageSnapshot() {
  return i18next.language;
}
function useLocale() {
  const locale = useSyncExternalStore4(
    subscribeLanguageChanged,
    getLanguageSnapshot
  );
  const locales = useSyncExternalStore4(
    subscribeConfig2,
    getSupportedLocalesSnapshot
  );
  useEffect4(() => {
    void bootstrapLocaleConfig();
  }, []);
  const setLocale = useCallback3(async (next) => {
    await i18next.changeLanguage(next);
    try {
      await updateMeApiV1AuthMePatch({
        body: { locale: next },
        throwOnError: true
      });
    } catch {
    }
  }, []);
  return { locale, supportedLocales: locales, setLocale };
}

// src/useDirection.ts
import { useSyncExternalStore as useSyncExternalStore5 } from "react";
import i18next2 from "i18next";
import { localeMeta } from "@forge/i18n";
function subscribeLanguageChanged2(listener) {
  i18next2.on("languageChanged", listener);
  return () => {
    i18next2.off("languageChanged", listener);
  };
}
function getDirectionSnapshot() {
  return localeMeta(i18next2.language)?.dir ?? "ltr";
}
function useDirection() {
  return useSyncExternalStore5(subscribeLanguageChanged2, getDirectionSnapshot);
}

// src/useSupportContext.ts
import { useQuery as useQuery2 } from "@tanstack/react-query";
import {
  getSupportContextApiV1MeSupportContextGet,
  getSupportContextApiV1MeSupportContextGetQueryKey
} from "@forge/api-client";
var STALE_TIME_MS2 = 3e5;
function useSupportContext() {
  const { user, isLoading } = useAuth();
  const { org } = useOrg();
  const { data } = useQuery2({
    // The endpoint takes no path params, so the generated key is a constant
    // -- the org rides in on the X-Org-ID header (orgInterceptor.ts) and the
    // user on the bearer token, neither of which the key would otherwise
    // see. Both are appended so switching org, or starting/ending an
    // impersonation session, is a normal cache miss rather than a stale read.
    queryKey: [
      ...getSupportContextApiV1MeSupportContextGetQueryKey(),
      user?.id ?? "",
      org?.id ?? ""
    ],
    queryFn: async () => {
      const { data: envelope } = await getSupportContextApiV1MeSupportContextGet({
        throwOnError: true
      });
      return envelope.data ?? null;
    },
    enabled: Boolean(user) && Boolean(org),
    staleTime: STALE_TIME_MS2
  });
  if (isLoading || !user || !org || !data) {
    return null;
  }
  return {
    userId: data.user_id,
    email: data.email,
    fullName: data.full_name,
    orgId: data.org_id,
    orgName: data.org_name,
    plan: data.plan,
    subscriptionStatus: data.subscription_status,
    signupAt: data.signup_at,
    locale: data.locale,
    isImpersonating: data.is_impersonating
  };
}

// src/useNotifications.ts
import { useCallback as useCallback4, useEffect as useEffect5, useRef, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery as useQuery3,
  useQueryClient
} from "@tanstack/react-query";
import {
  getUnreadCountApiV1NotificationsUnreadCountGet,
  getUnreadCountApiV1NotificationsUnreadCountGetQueryKey,
  listNotificationsApiV1NotificationsGet,
  listNotificationsApiV1NotificationsGetQueryKey,
  markAllNotificationsReadApiV1NotificationsReadAllPost,
  markNotificationReadApiV1NotificationsNotificationIdReadPatch,
  markNotificationUnreadApiV1NotificationsNotificationIdUnreadPatch
} from "@forge/api-client";
var DEFAULT_PAGE_SIZE = 50;
function mapNotifications(data, fn) {
  if (!data) {
    return data;
  }
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      data: page.data?.map(fn) ?? page.data
    }))
  };
}
function prependNotification(data, notification) {
  const [firstPage, ...restPages] = data?.pages ?? [];
  if (!data || !firstPage) {
    return data;
  }
  if (firstPage.data?.some((existing) => existing.id === notification.id)) {
    return data;
  }
  return {
    ...data,
    pages: [
      { ...firstPage, data: [notification, ...firstPage.data ?? []] },
      ...restPages
    ]
  };
}
function useNotifications(options = {}) {
  const { subscribe: subscribe3, pageSize = DEFAULT_PAGE_SIZE, onNotification } = options;
  const queryClient = useQueryClient();
  const [isConnected, setIsConnected] = useState(false);
  const hasConnectedOnceRef = useRef(false);
  const listQuery = useInfiniteQuery({
    queryKey: listNotificationsApiV1NotificationsGetQueryKey(),
    queryFn: async ({ pageParam }) => {
      const { data } = await listNotificationsApiV1NotificationsGet({
        query: { after: pageParam ?? void 0, limit: pageSize },
        throwOnError: true
      });
      return data;
    },
    initialPageParam: null,
    // undefined (not null) tells TanStack Query there is no next page --
    // the backend's cursor field is `string | null` once a list reaches its
    // end (§0.3 cursor pagination), so a bare `??` would otherwise hand
    // back `null` and leave hasNextPage stuck `true` forever.
    getNextPageParam: (lastPage) => lastPage.meta.next_cursor ?? void 0
  });
  const unreadCountQuery = useQuery3({
    queryKey: getUnreadCountApiV1NotificationsUnreadCountGetQueryKey(),
    queryFn: async () => {
      const { data } = await getUnreadCountApiV1NotificationsUnreadCountGet({
        throwOnError: true
      });
      return data;
    }
  });
  const markReadMutation = useMutation({
    mutationFn: async (notificationId) => {
      await markNotificationReadApiV1NotificationsNotificationIdReadPatch({
        path: { notification_id: notificationId },
        throwOnError: true
      });
    },
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({
        queryKey: listNotificationsApiV1NotificationsGetQueryKey()
      });
      const previous = queryClient.getQueryData(
        listNotificationsApiV1NotificationsGetQueryKey()
      );
      queryClient.setQueryData(
        listNotificationsApiV1NotificationsGetQueryKey(),
        (old) => mapNotifications(
          old,
          (notification) => notification.id === notificationId ? { ...notification, read_at: (/* @__PURE__ */ new Date()).toISOString() } : notification
        )
      );
      return { previous };
    },
    onError: (_error, _notificationId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          listNotificationsApiV1NotificationsGetQueryKey(),
          context.previous
        );
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: getUnreadCountApiV1NotificationsUnreadCountGetQueryKey()
      });
    }
  });
  const markUnreadMutation = useMutation({
    mutationFn: async (notificationId) => {
      await markNotificationUnreadApiV1NotificationsNotificationIdUnreadPatch({
        path: { notification_id: notificationId },
        throwOnError: true
      });
    },
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({
        queryKey: listNotificationsApiV1NotificationsGetQueryKey()
      });
      const previous = queryClient.getQueryData(
        listNotificationsApiV1NotificationsGetQueryKey()
      );
      queryClient.setQueryData(
        listNotificationsApiV1NotificationsGetQueryKey(),
        (old) => mapNotifications(
          old,
          (notification) => notification.id === notificationId ? { ...notification, read_at: null } : notification
        )
      );
      return { previous };
    },
    onError: (_error, _notificationId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          listNotificationsApiV1NotificationsGetQueryKey(),
          context.previous
        );
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: getUnreadCountApiV1NotificationsUnreadCountGetQueryKey()
      });
    }
  });
  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      await markAllNotificationsReadApiV1NotificationsReadAllPost({
        throwOnError: true
      });
    },
    onSuccess: () => {
      const now = (/* @__PURE__ */ new Date()).toISOString();
      queryClient.setQueryData(
        listNotificationsApiV1NotificationsGetQueryKey(),
        (old) => mapNotifications(
          old,
          (notification) => notification.read_at ? notification : { ...notification, read_at: now }
        )
      );
      void queryClient.invalidateQueries({
        queryKey: getUnreadCountApiV1NotificationsUnreadCountGetQueryKey()
      });
    }
  });
  const onNotificationRef = useRef(onNotification);
  onNotificationRef.current = onNotification;
  useEffect5(() => {
    if (!subscribe3) {
      return;
    }
    const unsubscribe = subscribe3({
      onNotification: (notification) => {
        queryClient.setQueryData(
          listNotificationsApiV1NotificationsGetQueryKey(),
          (old) => prependNotification(old, notification)
        );
        void queryClient.invalidateQueries({
          queryKey: getUnreadCountApiV1NotificationsUnreadCountGetQueryKey()
        });
        onNotificationRef.current?.(notification);
      },
      onConnectedChange: (connected) => {
        setIsConnected(connected);
        if (connected) {
          if (hasConnectedOnceRef.current) {
            void queryClient.invalidateQueries({
              queryKey: listNotificationsApiV1NotificationsGetQueryKey()
            });
            void queryClient.invalidateQueries({
              queryKey: getUnreadCountApiV1NotificationsUnreadCountGetQueryKey()
            });
          }
          hasConnectedOnceRef.current = true;
        }
      }
    });
    return unsubscribe;
  }, [subscribe3, queryClient]);
  const notifications = listQuery.data?.pages.flatMap((page) => page.data ?? []) ?? [];
  const loadMore = useCallback4(() => {
    void listQuery.fetchNextPage();
  }, [listQuery]);
  const markRead = useCallback4(
    async (id) => {
      await markReadMutation.mutateAsync(id);
    },
    [markReadMutation]
  );
  const markUnread = useCallback4(
    async (id) => {
      await markUnreadMutation.mutateAsync(id);
    },
    [markUnreadMutation]
  );
  const markAllRead = useCallback4(async () => {
    await markAllReadMutation.mutateAsync();
  }, [markAllReadMutation]);
  return {
    notifications,
    unreadCount: unreadCountQuery.data?.data?.count ?? 0,
    isLoading: listQuery.isLoading,
    isError: listQuery.isError,
    hasMore: listQuery.hasNextPage,
    isLoadingMore: listQuery.isFetchingNextPage,
    loadMore,
    markRead,
    markUnread,
    markAllRead,
    isConnected
  };
}
export {
  TwoFactorRequiredError,
  bootstrapModules,
  getModulesSnapshot,
  refreshConfig,
  useAuth,
  useDirection,
  useFeatureFlag,
  useFeatureFlags,
  useGlobalBanner,
  useLocale,
  useMaintenance,
  useMarketingUrl,
  useModules,
  useNotifications,
  useOAuthProviders,
  useOrg,
  usePermission,
  useSignupDisabled,
  useSupportContext,
  useSupportSettings
};
//# sourceMappingURL=index.js.map